'use client';

import { useEffect, useMemo, useRef } from 'react';
import useSWR from 'swr';
import { useUser } from '@clerk/clerk-react';

import { useAccounts } from '@/hooks/queries/useAccounts';
import { useCategoryOverview } from '@/hooks/queries/useCategoryOverview';
import { useGoalsOverview } from '@/hooks/queries/useGoalsOverview';
import { useRecurringOverview } from '@/hooks/queries/useRecurringPayments';
import { useSubscription } from '@/hooks/queries/useSubscription';
import { LONG_LIVED_SWR_OPTIONS, useAuthedFetcher } from '@/hooks/queries/swrConfig';
import { useCurrentMonthScope } from '@/hooks/useCurrentMonthScope';
import { investmentsApi, type InvestmentsPage } from '@/lib/api';
import { setPersonProperties, track } from '@/lib/telemetry';

// A user whose account is younger than this at first authenticated load just
// signed up — Clerk's <SignUp> has no completion callback and SSO signups skip
// the form entirely, so recency is the reliable cross-flow signal.
const SIGNUP_RECENCY_MS = 30 * 60 * 1000;

function signupTrackedKey(userId: string): string {
  return `vertice.telemetry.signupTracked.${userId}`;
}

/**
 * Keeps analytics person properties (demographics) in sync with what the app
 * already knows, and emits `auth.signed_up` once for brand-new users. Renders
 * nothing; mounts once in the dashboard layout.
 *
 * Privacy: counts, booleans, enums, bank names, and the account-creation
 * timestamp (`signed_up_at`, for tenure cohorts) — never balances, names,
 * emails, or documents. Values are diffed so PostHog only receives a `$set`
 * when something actually changed.
 */
export function PersonPropertiesSync() {
  const { user } = useUser();
  const authedFetch = useAuthedFetcher();

  const accounts = useAccounts();
  const goals = useGoalsOverview();
  const monthScope = useCurrentMonthScope();
  const overviewScope = useMemo(
    () => ({ year: monthScope.year, month: monthScope.month }),
    [monthScope],
  );
  const budgets = useCategoryOverview(overviewScope);
  const recurring = useRecurringOverview();
  const subscription = useSubscription();
  // One row is enough: `total` counts the whole live portfolio. Keyed by user and never carried
  // over, so another account signing in is not given this one's answer.
  const investments = useSWR<InvestmentsPage>(
    user?.id ? ['person-props-investments', user.id] : null,
    () => authedFetch((token) => investmentsApi.getAll(token, { limit: 1 })),
    { ...LONG_LIVED_SWR_OPTIONS, keepPreviousData: false },
  );

  // Each block contributes only once its query settled — partial `$set`s merge
  // server-side, so early properties don't wait for the slowest endpoint.
  const properties = useMemo(() => {
    const props: Record<string, unknown> = {};

    if (accounts.data) {
      props.accounts_count = accounts.data.length;
      props.cards_count = accounts.data.filter((a) => a.type === 'CREDIT').length;
      props.connections_count = new Set(accounts.data.map((a) => a.itemId)).size;
      props.connector_names = [
        ...new Set(accounts.data.map((a) => a.item?.connectorName).filter(Boolean)),
      ].sort();
    }
    if (goals.data) {
      props.goals_count = goals.data.goals.length;
      props.has_goals = goals.data.goals.length > 0;
    }
    if (budgets.data) {
      props.has_budgets = (budgets.data.summary?.totalBudgeted ?? 0) > 0;
    }
    if (recurring.data) {
      props.has_recurring = recurring.data.groups.length > 0;
    }
    if (investments.data) {
      props.has_investments = investments.data.positions.total > 0;
    }
    if (subscription.data) {
      props.plan_code = subscription.data.planCode;
      props.subscription_status = subscription.data.status;
    }
    if (user?.createdAt) {
      props.signed_up_at = user.createdAt.toISOString();
    }

    return props;
  }, [
    accounts.data,
    goals.data,
    budgets.data,
    recurring.data,
    investments.data,
    subscription.data,
    user?.createdAt,
  ]);

  const lastSentRef = useRef<string | null>(null);
  useEffect(() => {
    if (Object.keys(properties).length === 0) return;
    const fingerprint = JSON.stringify(properties);
    if (fingerprint === lastSentRef.current) return;
    lastSentRef.current = fingerprint;
    setPersonProperties(properties);
  }, [properties]);

  useEffect(() => {
    if (!user?.id || !user.createdAt) return;
    if (Date.now() - user.createdAt.getTime() > SIGNUP_RECENCY_MS) return;
    try {
      const key = signupTrackedKey(user.id);
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
    } catch {
      // Storage blocked: still track — PostHog "first time" math dedupes.
    }
    track('action.auth.sign_up');
  }, [user?.id, user?.createdAt]);

  return null;
}
